import { Operation } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { getMainDefinition } from "@apollo/client/utilities";

// Waits before the 1st, 2nd and 3rd retry; after that the error is shown.
const RETRY_DELAYS_MS = [300, 1000, 3000];

// Gateway errors while the API restarts (e.g. during a deploy).
const RETRYABLE_STATUS_CODES = [502, 503, 504];

const isQuery = (operation: Operation): boolean => {
  const definition = getMainDefinition(operation.query);

  return definition.kind === "OperationDefinition" && definition.operation === "query";
};

// Only failures where the request never got a real answer: the connection
// dropped ("Failed to fetch") or the gateway had no API behind it. Answers
// from the API itself (permission denied, invalid input) are never retried.
export const isNetworkFailure = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const { name, statusCode } = error as { name?: string; statusCode?: number };

  if (name === "AbortError") {
    return false;
  }

  if (typeof statusCode === "number") {
    return RETRYABLE_STATUS_CODES.includes(statusCode);
  }

  return true;
};

export const shouldRetry = (count: number, operation: Operation, error: unknown): boolean =>
  count <= RETRY_DELAYS_MS.length && isQuery(operation) && isNetworkFailure(error);

export const retryDelay = (count: number): number =>
  RETRY_DELAYS_MS[Math.min(count, RETRY_DELAYS_MS.length) - 1];

// Retries reads that fail on the network, so a Wi-Fi blip or an API restart
// doesn't surface as an error. Mutations are sent exactly once: retrying one
// could repeat it (e.g. mint a second batch code) when the first got through.
export const retryLink = new RetryLink({
  attempts: shouldRetry,
  delay: retryDelay,
});
